package co.maytes.standupbot.dashboard.features.onboarding

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.AnimatedCheck
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideAlertCircle
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

enum class WizardBrand(val asset: String, val label: String) {
    Slack("/brand/slack.svg", "Slack"),
    Linear("/brand/linear.svg", "Linear"),
    GitHub("/brand/github.svg", "GitHub"),
    Claude("/brand/claude.svg", "Claude"),
    Google("/brand/google.svg", "Google"),
    StandupBot("/logo-square.svg", "StandupBot"),
}

enum class ChipSize(val cssClass: String?) {
    Sm("sb-brand-chip--sm"),
    Md(null),
    Lg("sb-brand-chip--lg"),
}

@Composable
fun BrandChip(brand: WizardBrand, size: ChipSize = ChipSize.Md, badge: Boolean = false) {
    Div(
        attrs = {
            classes("sb-brand-chip")
            size.cssClass?.let { classes(it) }
            if (brand == WizardBrand.StandupBot) classes("sb-brand-chip--bleed")
        },
    ) {
        Img(src = brand.asset, alt = brand.label)
        if (badge) {
            Div(attrs = { classes("sb-brand-badge") }) { AnimatedCheck(sizePx = 10) }
        }
    }
}

data class FlowNode(val brand: WizardBrand, val live: Boolean = false)

@Composable
fun WizardFlow(nodes: List<FlowNode>, flowing: Boolean = false) {
    Div(
        attrs = {
            classes("sb-wiz-flow")
            attr("role", "img")
            attr("aria-label", nodes.joinToString(" to ") { it.brand.label })
        },
    ) {
        nodes.forEachIndexed { index, node ->
            if (index > 0) {
                val live = nodes[index - 1].live && node.live
                Div(
                    attrs = {
                        classes("sb-wiz-link")
                        when {
                            live -> classes("sb-wiz-link--live")
                            flowing -> classes("sb-wiz-link--flowing")
                        }
                    },
                )
            }
            BrandChip(
                brand = node.brand,
                size = ChipSize.Lg,
                badge = node.live && node.brand != WizardBrand.StandupBot,
            )
        }
    }
}

enum class ChipTone(val cssClass: String) {
    Neutral("sb-wiz-chip--neutral"),
    Success("sb-wiz-chip--success"),
    Danger("sb-wiz-chip--danger"),
}

@Composable
fun StatusChip(label: String, tone: ChipTone) {
    Span(attrs = { classes("sb-wiz-chip", tone.cssClass) }) {
        if (tone == ChipTone.Success) AnimatedCheck(sizePx = 12)
        Text(label)
    }
}

enum class NoticeTone(val cssClass: String?) {
    Info(null),
    Success("sb-wiz-notice--success"),
    Danger("sb-wiz-notice--danger"),
}

@Composable
fun Seal(danger: Boolean = false, large: Boolean = false) {
    Div(
        attrs = {
            classes("sb-wiz-seal")
            if (danger) classes("sb-wiz-seal--danger")
            if (large) classes("sb-wiz-seal--large")
        },
    ) {
        if (danger) LucideAlertCircle(size = 18) else AnimatedCheck(sizePx = if (large) 28 else 16)
    }
}

@Composable
fun WizardNotice(
    tone: NoticeTone,
    title: String,
    message: String? = null,
    action: (@Composable () -> Unit)? = null,
) {
    Div(
        attrs = {
            classes("sb-wiz-notice")
            tone.cssClass?.let { classes(it) }
            attr("role", if (tone == NoticeTone.Danger) "alert" else "status")
        },
    ) {
        when (tone) {
            NoticeTone.Success -> Seal()
            NoticeTone.Danger -> Seal(danger = true)
            NoticeTone.Info -> Unit
        }
        Div(attrs = { classes("sb-wiz-notice-text") }) {
            Span(attrs = { classes("sb-wiz-notice-title") }) { Text(title) }
            message?.let { Span(attrs = { classes("sb-wiz-notice-message") }) { Text(it) } }
        }
        action?.let { Div(attrs = { classes("sb-wiz-notice-action") }) { it() } }
    }
}

@Composable
fun InlineError(title: String, message: String, onRetry: () -> Unit) {
    WizardNotice(
        tone = NoticeTone.Danger,
        title = title,
        message = message,
        action = { Button(label = "Retry", variant = ButtonVariant.Secondary, size = ButtonSize.Sm, onClick = onRetry) },
    )
}
